import React from 'react'
import { Box, Skeleton, Group, Card } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { Stack } from '@mantine/core'

interface HorizontalGameScrollerSkeletonProps {
    count?: number
}

export const HorizontalGameScrollerSkeleton: React.FC<HorizontalGameScrollerSkeletonProps> = ({
    count = 6
}) => {
    // Use media queries to determine card width - same as HorizontalGameScroller
    const isXS = useMediaQuery('(max-width: 430px)') // Extra small screens (phones in portrait)
    const isMobile = useMediaQuery('(max-width: 768px)')
    const isTablet = useMediaQuery('(max-width: 992px)')

    // Calculate card width based on screen size to match homepage grid
    const getCardWidth = () => {
        if (isXS) {
            return 'calc(50% - 0.5rem)'
        } else if (isMobile) {
            return 'calc(50% - 0.5rem)'
        } else if (isTablet) {
            return 'calc(33.333% - 0.67rem)'
        } else {
            return 'calc(25% - 0.75rem)'
        }
    }

    return (
        <Box style={{ position: 'relative' }}>
            {/* Scrollable Container */}
            <Box
                style={{
                    display: 'flex',
                    gap: '1.5rem',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    paddingBottom: '1rem',
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE/Edge
                }}
                className="hide-scrollbar"
            >
                {Array.from({ length: count }).map((_, index) => (
                    <Box
                        key={index}
                        style={{
                            minWidth: getCardWidth(),
                            maxWidth: getCardWidth(),
                            flexShrink: 0,
                        }}
                    >
                        {/* Game Card Skeleton */}
                        <Card
                            shadow="none"
                            radius="md"
                            withBorder={false}
                            p={0}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '340px',
                                backgroundColor: 'transparent'
                            }}
                        >
                            {/* Image section - matches Card.Section exactly */}
                            <Card.Section p="md" style={{ position: 'relative', flex: 1, minHeight: 0, maxHeight: '280px' }}>
                                <Skeleton height="100%" radius="md" />
                            </Card.Section>

                            {/* Content section - matches the actual content structure exactly */}
                            <div style={{ flexShrink: 0, marginBottom: 'auto', padding: '0 0' }}>
                                <Stack gap="xs">
                                    {/* Game title skeleton */}
                                    <Skeleton height={20} width="90%" radius="sm" />

                                    {/* Price section skeleton */}
                                    <Group gap="xs" align="baseline">
                                        <Skeleton height={24} width={80} radius="sm" />
                                        <Skeleton height={16} width={60} radius="sm" />
                                    </Group>
                                </Stack>
                            </div>
                        </Card>
                    </Box>
                ))}
            </Box>

            {/* Scroll Indicators */}
            <Group justify="center" mt="xs">
                {Array.from({ length: Math.ceil(count / (isXS ? 1.5 : isMobile ? 2 : isTablet ? 3 : 4)) }).map((_, index) => (
                    <Skeleton
                        key={index}
                        width={8}
                        height={8}
                        radius="50%"
                    />
                ))}
            </Group>
        </Box>
    )
} 